import { Image as ImageIcon, Video, FileText, ExternalLink, Figma, BookOpen, Music, Lock } from "lucide-react";
import { fileUrl, videoEmbedUrl, figmaEmbedUrl, normalizePiece, WORK_TYPE_LABEL, PIECE_TYPES } from "@/lib/portfolio";

const ICONS = {
  image: ImageIcon, video: Video, pdf: FileText, url: ExternalLink,
  figma: Figma, case_study: BookOpen, audio: Music, on_request: Lock,
};

export default function PortfolioPieceRenderer({ piece: rawPiece, index, compact = false }) {
  const piece = normalizePiece(rawPiece);
  const Icon = ICONS[piece.piece_type] || ImageIcon;
  const typeLabel = PIECE_TYPES.find((t) => t.type === piece.piece_type)?.label || piece.piece_type;

  return (
    <article className="brut-tile p-5 md:p-8 group" data-testid={`portfolio-piece-${index}`}>
      <div className="flex items-start justify-between mb-4 gap-2 sm:gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="terminal-label">/ {String((index ?? 0) + 1).padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground inline-flex items-center gap-1.5">
            <Icon className="w-3 h-3" strokeWidth={1.5} />
            {typeLabel}
          </div>
        </div>
        <div className="serif-mod text-base sm:text-xl text-foreground/30 truncate max-w-[55%] sm:max-w-none" style={{ fontWeight: 400 }}>
          {WORK_TYPE_LABEL[piece.work_type] || piece.work_type?.replace(/_/g, " ")}
        </div>
      </div>

      <PieceMedia piece={piece} compact={compact} />

      <div className="mt-5">
        <h2 className="serif-mod text-xl sm:text-2xl md:text-3xl tracking-tight mb-3 break-words" style={{ fontWeight: 400 }}>
          {piece.title}
          {piece.visibility === "on_request" && (
            <span className="ml-2 sm:ml-3 align-middle inline-block text-[10px] tracking-[0.22em] uppercase text-primary border border-primary px-2 py-0.5 font-mono mt-1" data-testid={`portfolio-piece-${index}-onreq-badge`}>
              On request
            </span>
          )}
        </h2>
        {piece.description && <p className="text-sm text-foreground/75 leading-relaxed">{piece.description}</p>}

        {piece.piece_type === "case_study" && piece.case_study && (
          <CaseStudyBody cs={piece.case_study} index={index} />
        )}

        <PieceLink piece={piece} index={index} />
      </div>
    </article>
  );
}

function PieceMedia({ piece, compact }) {
  switch (piece.piece_type) {
    case "image": {
      const paths = piece.image_paths?.length ? piece.image_paths : (piece.image_path ? [piece.image_path] : []);
      if (!paths.length) return null;
      if (paths.length === 1) {
        return (
          <div className="aspect-video bg-surface overflow-hidden">
            <img src={fileUrl(paths[0])} alt={piece.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          </div>
        );
      }
      return (
        <div className={`grid ${compact ? "grid-cols-3" : "grid-cols-2 md:grid-cols-3"} gap-2`}>
          {paths.slice(0, compact ? 3 : 6).map((p) => (
            <div key={p} className="aspect-square bg-surface overflow-hidden">
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
          ))}
        </div>
      );
    }
    case "video": {
      const embed = videoEmbedUrl(piece.video_url);
      if (!embed) return piece.video_url ? <ExternalFallback url={piece.video_url} label="Open video" /> : null;
      return (
        <div className="aspect-video bg-surface">
          <iframe src={embed} title={piece.title || "Video"} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      );
    }
    case "pdf": {
      const url = fileUrl(piece.pdf_path);
      if (!url) return null;
      return (
        <div className="aspect-[4/5] bg-surface border border-border">
          <iframe src={url} title={piece.title || "PDF"} className="w-full h-full" />
        </div>
      );
    }
    case "url": {
      if (!piece.external_url) return null;
      let host = piece.external_url;
      try { host = new URL(piece.external_url).hostname.replace(/^www\./, ""); } catch {
        // not a parseable URL - fall back to raw string
      }
      return (
        <a href={piece.external_url} target="_blank" rel="noopener noreferrer" className="block panel p-5 hover:border-primary transition-colors">
          <div className="flex items-center gap-3">
            <ExternalLink className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <div className="min-w-0">
              <div className="serif-mod text-base truncate" style={{ fontWeight: 500 }}>{host}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{piece.external_url}</div>
            </div>
          </div>
        </a>
      );
    }
    case "figma": {
      const embed = figmaEmbedUrl(piece.figma_url);
      if (!embed) return piece.figma_url ? <ExternalFallback url={piece.figma_url} label="Open in Figma" /> : null;
      return (
        <div className="aspect-video bg-surface border border-border">
          <iframe src={embed} title={piece.title || "Figma"} className="w-full h-full" allowFullScreen />
        </div>
      );
    }
    case "audio": {
      const url = fileUrl(piece.audio_path);
      if (!url) return null;
      return (
        <div className="panel p-4 flex items-center gap-3">
          <Music className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5} />
          <audio src={url} controls className="w-full" preload="metadata">Your browser does not support audio.</audio>
        </div>
      );
    }
    case "case_study": {
      // Hero image: prefer the first problem image, then process, then outcome.
      const cs = piece.case_study || {};
      const hero = cs.problem?.image_paths?.[0] || cs.process?.image_paths?.[0] || cs.outcome?.image_paths?.[0];
      if (!hero) return null;
      return (
        <div className="aspect-video bg-surface overflow-hidden">
          <img src={fileUrl(hero)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      );
    }
    case "on_request":
      return (
        <div className="border border-dashed border-foreground/20 p-6 text-center" data-testid="onrequest-placeholder">
          <Lock className="w-5 h-5 text-primary mx-auto mb-2" strokeWidth={1.5} />
          <div className="text-[10px] tracking-[0.22em] uppercase text-foreground/65 font-mono">Available on request</div>
        </div>
      );
    default:
      return null;
  }
}

function CaseStudyBody({ cs, index }) {
  const sections = [
    { key: "problem", label: "The problem" },
    { key: "process", label: "The process" },
    { key: "outcome", label: "The outcome" },
  ];
  return (
    <div className="mt-6 space-y-6 border-t border-foreground/15 pt-6">
      {sections.map(({ key, label }) => {
        const s = cs[key] || {};
        if (!s.text && !(s.image_paths || []).length) return null;
        return (
          <div key={key} data-testid={`portfolio-piece-${index}-cs-${key}`}>
            <div className="terminal-label text-primary mb-2">{label}</div>
            {s.text && <p className="text-sm text-foreground/80 leading-relaxed mb-3">{s.text}</p>}
            {(s.image_paths || []).length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {s.image_paths.map((p) => (
                  <div key={p} className="aspect-square bg-surface overflow-hidden">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PieceLink({ piece, index }) {
  let href = null;
  let label = "View project";
  if (piece.piece_type === "video" && piece.video_url) { href = piece.video_url; label = "Open original"; }
  else if (piece.piece_type === "url" && piece.external_url) { href = piece.external_url; label = "Visit"; }
  else if (piece.piece_type === "pdf" && piece.pdf_path) { href = fileUrl(piece.pdf_path); label = "Open PDF"; }
  else if (piece.piece_type === "figma" && piece.figma_url) { href = piece.figma_url; label = "Open in Figma"; }
  else if (piece.piece_type === "audio" && piece.audio_path) { href = fileUrl(piece.audio_path); label = "Download audio"; }
  else if (piece.link) { href = piece.link; label = "View project"; }
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="mt-5 inline-block btn-secondary text-[10px]" data-testid={`portfolio-piece-link-${index}`}>
      {label} →
    </a>
  );
}

function ExternalFallback({ url, label }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block panel p-4 text-center hover:border-primary">
      <ExternalLink className="w-4 h-4 mx-auto mb-2 text-primary" />
      <div className="text-[10px] uppercase tracking-[0.18em]">{label}</div>
    </a>
  );
}
